import React, { useId } from 'react';
import { motion } from 'framer-motion';
import { Label } from '../../Label';
import { Checkbox } from '../../Checkbox';
import { RadioGroup, RadioGroupItem } from '../../Radio';
const ENTER_EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]';
interface FieldWrapperProps {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}
function FieldWrapper({
  label,
  htmlFor,
  children
}: FieldWrapperProps) {
  return <div className="flex flex-col gap-2.5" data-id="element-39">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-[hsl(var(--foreground))]" data-id="element-40">
        {label}
      </Label>
      {children}
    </div>;
}
export function RadioRow({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (val: string) => void;
}) {
  return <FieldWrapper label={label} data-id="element-41">
      <RadioGroup value={value} onValueChange={onChange} aria-label={label} className="flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-id="element-42">
        {options.map(opt => {
        const selected = value === opt;
        return <label key={opt} className={[`flex cursor-pointer items-center gap-2 rounded-[var(--radius)] border px-3 py-2 text-sm transition duration-150 ${ENTER_EASE} active:scale-[0.98]`, selected ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.04] text-[hsl(var(--foreground))]' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50'].join(' ')} data-id="element-43">
              <RadioGroupItem value={opt} data-id="element-44" />
              {opt}
            </label>;
      })}
      </RadioGroup>
    </FieldWrapper>;
}
export function SliderField({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  formatValue
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  formatValue: (val: number) => string;
}) {
  const id = useId();
  return <FieldWrapper label={label} htmlFor={id} data-id="element-45">
      <div className="flex items-center gap-4" data-id="element-46">
        <input id={id} type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))} className="h-2 w-full cursor-pointer appearance-none rounded-full bg-[hsl(var(--muted))] accent-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2" data-id="element-47" />
        <span className="min-w-[4rem] text-right text-sm font-medium tabular-nums text-[hsl(var(--muted-foreground))]" data-id="element-48">
          {formatValue(value)}
        </span>
      </div>
    </FieldWrapper>;
}
export function CheckboxGroup({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (val: string[]) => void;
}) {
  const toggle = (opt: string, checked: boolean) => {
    if (checked) onChange([...value, opt]);else onChange(value.filter(v => v !== opt));
  };
  return <FieldWrapper label={label} data-id="element-49">
      <div role="group" aria-label={label} className="flex flex-col gap-2" data-id="element-50">
        {options.map(opt => <label key={opt} className="flex cursor-pointer items-start gap-3" data-id="element-51">
            <Checkbox checked={value.includes(opt)} onCheckedChange={checked => toggle(opt, checked === true)} className="mt-0.5" data-id="element-52" />
            <span className="text-sm text-[hsl(var(--foreground))]" data-id="element-53">{opt}</span>
          </label>)}
      </div>
    </FieldWrapper>;
}
export function TriToggle({
  label,
  value,
  onChange
}: {
  label: string;
  value: 'Yes' | 'No' | 'Unsure';
  onChange: (val: 'Yes' | 'No' | 'Unsure') => void;
}) {
  const pillId = useId();
  const options = ['Yes', 'No', 'Unsure'] as const;
  return <FieldWrapper label={label} data-id="element-54">
      <div role="group" aria-label={label} className="inline-flex self-start rounded-[var(--radius)] bg-[hsl(var(--muted))] p-1" data-id="element-55">
        {options.map(opt => {
        const active = value === opt;
        return <button key={opt} type="button" aria-pressed={active} onClick={() => onChange(opt)} className={['relative z-10 rounded-[calc(var(--radius)-0.25rem)] px-4 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--muted))]', active ? 'text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'].join(' ')} data-id="element-56">
              {active && <motion.span layoutId={`tri-pill-${pillId}`} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 38
          }} className="absolute inset-0 -z-10 rounded-[calc(var(--radius)-0.25rem)] bg-[hsl(var(--background))] shadow-sm" data-id="element-57" />}
              {opt}
            </button>;
      })}
      </div>
    </FieldWrapper>;
}
export function RatingScale({
  label,
  value,
  onChange
}: {
  label: string;
  value: number | null;
  onChange: (val: number) => void;
}) {
  return <FieldWrapper label={label} data-id="element-58">
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5" data-id="element-59">
        {Array.from({
        length: 10
      }, (_, i) => i + 1).map(num => <button key={num} type="button" aria-pressed={value === num} aria-label={`Rating ${num} of 10`} onClick={() => onChange(num)} className={[`flex h-9 w-9 items-center justify-center rounded-[var(--radius)] border text-sm font-medium transition duration-150 ${ENTER_EASE} active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2`, value === num ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]'].join(' ')} data-id="element-60">
            {num}
          </button>)}
      </div>
    </FieldWrapper>;
}